import React, { useState } from 'react';
import { useQuery, useAction, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useNavigate } from 'react-router-dom';
import { Id } from '../../convex/_generated/dataModel';

type FilterType = 'all' | 'pending' | 'migrated' | 'expired' | 'changed' | 'expiring_soon';

interface MigrationUser {
  _id: Id<"users">;
  username: string;
  name?: string;
  email?: string;
  passwordMigratedAt?: number;
  passwordExpiresAt?: number;
  mustChangePassword?: boolean;
  status: string;
}

const PasswordMigrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [csvData, setCsvData] = useState<string>('');
  const [migrationStatus, setMigrationStatus] = useState<{
    running: boolean;
    result?: {
      processed: number;
      succeeded: number;
      failed: number;
      skipped: number;
      errors: Array<{ username: string; error: string }>;
    };
  }>({ running: false });

  const ITEMS_PER_PAGE = 20;

  // Queries
  const stats = useQuery(api.passwordMigrationQueries.getMigrationStats);
  const details = useQuery(api.passwordMigrationQueries.getMigrationDetails, {
    filter,
    search: search || undefined,
    limit: ITEMS_PER_PAGE,
    offset: page * ITEMS_PER_PAGE,
  });

  // Actions and mutations
  const processMigration = useAction(api.passwordMigration.processMigrationCSV);
  const extendExpiration = useMutation(api.passwordMigrationQueries.extendExpiration);
  const resetMigration = useMutation(api.passwordMigrationQueries.resetMigrationStatus);

  const handleCSVUpload = async () => {
    if (!csvData.trim()) {
      alert('Please paste CSV data');
      return;
    }

    // Parse CSV
    const lines = csvData.trim().split('\n');
    const header = lines[0].toLowerCase().split(',').map(h => h.trim());
    const siteNameIndex = header.findIndex(h => h === 'site_name' || h === 'username');
    const serialIndex = header.findIndex(h => h === 'serial_number' || h === 'meter_serial');

    if (siteNameIndex === -1 || serialIndex === -1) {
      alert('CSV must have columns: site_name (or username) and serial_number (or meter_serial)');
      return;
    }

    const data = lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim());
      return {
        site_name: values[siteNameIndex],
        serial_number: values[serialIndex],
      };
    }).filter(row => row.site_name && row.serial_number);

    if (data.length === 0) {
      alert('No valid data found in CSV');
      return;
    }

    if (!confirm(`This will migrate ${data.length} users. Continue?`)) {
      return;
    }

    setMigrationStatus({ running: true });

    try {
      const result = await processMigration({ csvData: data });
      setMigrationStatus({ running: false, result });
    } catch (error: any) {
      alert('Migration failed: ' + error.message);
      setMigrationStatus({ running: false });
    }
  };

  const handleExtendExpiration = async (userId: Id<"users">, days: number) => {
    try {
      await extendExpiration({ userId, additionalDays: days });
    } catch (error: any) {
      alert('Failed to extend: ' + error.message);
    }
  };

  const handleResetMigration = async (userId: Id<"users">) => {
    if (!confirm('This will force the user to change their password again. Continue?')) {
      return;
    }
    try {
      await resetMigration({ userId });
    } catch (error: any) {
      alert('Failed to reset: ' + error.message);
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-800',
      migrated: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-red-100 text-red-800',
      changed: 'bg-green-100 text-green-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto"><div className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-shade-green-500 transition-colors duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Admin
          </button>
        </div>
      <h1 className="text-2xl font-bold mb-6">Password Migration Dashboard</h1>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-gray-900">{stats.totalUsers}</div>
            <div className="text-sm text-gray-500">Total Users</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-blue-600">{stats.migrated}</div>
            <div className="text-sm text-gray-500">Migrated</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-green-600">{stats.passwordsChanged}</div>
            <div className="text-sm text-gray-500">Changed Password</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-gray-600">{stats.pendingMigration}</div>
            <div className="text-sm text-gray-500">Pending</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
            <div className="text-sm text-gray-500">Expired</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-orange-600">{stats.expiringThisWeek}</div>
            <div className="text-sm text-gray-500">Expiring This Week</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-yellow-600">{stats.expiringThisMonth}</div>
            <div className="text-sm text-gray-500">Expiring This Month</div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {stats && stats.migrated > 0 && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span>Migration Progress</span>
            <span>
              {stats.passwordsChanged} / {stats.migrated} changed
              ({stats.migrated > 0 ? Math.round((stats.passwordsChanged / stats.migrated) * 100) : 0}%)
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-green-500 h-4 rounded-full transition-all"
              style={{
                width: `${stats.migrated > 0 ? (stats.passwordsChanged / stats.migrated) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* CSV Upload Section */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-medium font-sans mb-3">Bulk Migration</h2>
        <p className="text-sm text-gray-600 mb-3">
          Paste CSV data with columns: <code className="bg-gray-100 px-1">site_name</code> and{' '}
          <code className="bg-gray-100 px-1">serial_number</code>
        </p>
        <textarea
          value={csvData}
          onChange={(e) => setCsvData(e.target.value)}
          placeholder="site_name,serial_number&#10;AARO21332,ABC123456&#10;MARS4142,XYZ789012"
          className="w-full h-32 p-2 border border-gray-300 rounded-md font-mono text-sm mb-3"
        />
        <button
          onClick={handleCSVUpload}
          disabled={migrationStatus.running || !csvData.trim()}
          className="bg-[#16A34A]  text-white px-4 py-2 rounded-md hover:bg-shade-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {migrationStatus.running ? 'Processing...' : 'Start Migration'}
        </button>

        {/* Migration Results */}
        {migrationStatus.result && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <h3 className="font-medium font-sans mb-2">Migration Complete</h3>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Processed:</span>{' '}
                <span className="font-medium">{migrationStatus.result.processed}</span>
              </div>
              <div>
                <span className="text-green-600">Succeeded:</span>{' '}
                <span className="font-medium">{migrationStatus.result.succeeded}</span>
              </div>
              <div>
                <span className="text-red-600">Failed:</span>{' '}
                <span className="font-medium">{migrationStatus.result.failed}</span>
              </div>
              <div>
                <span className="text-yellow-600">Skipped:</span>{' '}
                <span className="font-medium">{migrationStatus.result.skipped}</span>
              </div>
            </div>
            {migrationStatus.result.errors.length > 0 && (
              <div className="mt-3">
                <h4 className="text-sm font-medium text-red-600 mb-1">Errors:</h4>
                <ul className="text-sm text-gray-600 max-h-32 overflow-y-auto">
                  {migrationStatus.result.errors.map((err, i) => (
                    <li key={i} className="text-red-600">
                      {err.username}: {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Filter */}
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as FilterType);
                setPage(0);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Users</option>
              <option value="pending">Pending Migration</option>
              <option value="migrated">Migrated (Not Changed)</option>
              <option value="changed">Password Changed</option>
              <option value="expired">Expired</option>
              <option value="expiring_soon">Expiring Soon</option>
            </select>

            {/* Search */}
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search username, name, or email..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Migration Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {details?.users.map((user: MigrationUser) => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.username}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(user.passwordMigratedAt)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(user.passwordExpiresAt)}</td>
                  <td className="px-4 py-3">{getStatusBadge(user.status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {user.status !== 'pending' && user.status !== 'changed' && (
                        <>
                          <button
                            onClick={() => handleExtendExpiration(user._id, 30)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            +30 days
                          </button>
                          <button
                            onClick={() => handleResetMigration(user._id)}
                            className="text-xs text-orange-600 hover:text-orange-800"
                          >
                            Force Change
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {details?.users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {details && (
          <div className="p-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {page * ITEMS_PER_PAGE + 1} -{' '}
              {Math.min((page + 1) * ITEMS_PER_PAGE, details.total)} of {details.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={!details.hasMore}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordMigrationPage;
